import { AlertCircle, Check } from "lucide-react";
import React, { useState } from "react";
import { CashBreakdownModal } from "@/components/modals/CashBreakdownModal";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Card, CardContent } from "@/components/ui/card";
import { useDataSourceContext } from "@/contexts/DataSourceContext";
import { DEMO_BANK_ACCOUNTS } from "@/data/demo-data";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";
import { useTerminology } from "@/hooks/useTerminology";
import { getDashboardMetrics } from "@/services/metricsService";

export default function CeoCashPositionCard() {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { getCashDurationTerm, getForecastTerm } = useTerminology();
  const { dataSources } = useDataSourceContext();
  const [showCashModal, setShowCashModal] = useState(false);
  const [cashHovered, setCashHovered] = useState(false);

  if (!hasPermission("view_executive_cash_position_card")) return null;

  const metrics = getDashboardMetrics(dataSources);

  // Traffic light based on runway
  const getBadgeStyle = () => {
    if (metrics.runway >= 3)
      return {
        bg: "var(--mc-success-bg)",
        icon: "var(--mc-success-icon)",
        border: "var(--mc-success-border)",
        label: "Healthy",
      };
    if (metrics.runway >= 1)
      return {
        bg: "var(--mc-warning-bg)",
        icon: "var(--mc-warning-icon)",
        border: "var(--mc-warning-border)",
        label: "Caution",
      };
    return {
      bg: "var(--mc-error-bg)",
      icon: "var(--mc-error-icon)",
      border: "var(--mc-error-border)",
      label: "At Risk",
    };
  };

  const badge = getBadgeStyle();
  const durationTerm = getCashDurationTerm(metrics.runway);

  return (
    <>
      <Card
        className="h-full mx-auto"
        style={{
          borderLeft: `3px solid ${badge.border}`,
          maxWidth: "455px",
          width: "100%",
        }}
      >
        <CardContent
          className="p-6 flex flex-col gap-6"
          style={{ minHeight: "520px" }}
        >
          {/* Title */}
          <div className="flex items-center gap-1">
            <span className="text-lg font-semibold text-neutral-900">
              Cash Position
            </span>
            <InfoTooltip
              title="Cash Position"
              content={`Your total available cash across all connected bank accounts, ${getForecastTerm(false)} positions, and upcoming obligations.`}
            />
          </div>

          {/* Central cash balance */}
          <div className="flex flex-col items-center gap-1 py-6">
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: click handler only */}
            <span
              className="text-4xl font-bold text-neutral-900 tracking-tight"
              style={{
                cursor: "pointer",
                textDecoration: cashHovered ? "underline" : "none",
                textUnderlineOffset: 3,
              }}
              onClick={() => setShowCashModal(true)}
              onMouseEnter={() => setCashHovered(true)}
              onMouseLeave={() => setCashHovered(false)}
            >
              {formatCurrency(metrics.currentCash)}
            </span>
            <span className="text-sm text-neutral-900">
              Current Cash Balance
            </span>
          </div>

          {/* Health badge + coverage */}
          <div
            className="flex items-center gap-2 rounded-lg px-3 py-2"
            style={{
              backgroundColor: badge.bg,
              border: `1px solid ${badge.border}`,
            }}
          >
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: badge.icon }}
            >
              <Check className="w-3 h-3 text-white" />
            </div>
            <div>
              <span
                className="text-sm font-semibold"
                style={{ color: badge.icon }}
              >
                {badge.label}
              </span>
              <span className="text-sm ml-1" style={{ color: badge.icon }}>
                {durationTerm}
              </span>
            </div>
          </div>

          {/* 30-day and 90-day forecast boxes */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-neutral-200 px-3 py-2 text-center">
              <p className="text-base font-semibold text-neutral-900">
                {formatCurrency(metrics.projectedCash30Days)}
              </p>
              <p className="text-xs text-neutral-900 mt-0.5">
                30-Day {getForecastTerm(true)}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 px-3 py-2 text-center">
              <p className="text-base font-semibold text-neutral-900">
                {formatCurrency(metrics.projectedCash90Days)}
              </p>
              <p className="text-xs text-neutral-900 mt-0.5">
                90-Day {getForecastTerm(true)}
              </p>
            </div>
          </div>

          {/* Overdue warning bar */}
          {metrics.overdueInvoices > 0 && (
            <>
              <hr className="border-neutral-200" />
              <div
                className="flex items-center gap-2 rounded-lg px-3 py-2"
                style={{
                  backgroundColor: "var(--mc-warning-bg)",
                  border: "1px solid var(--mc-warning-border)",
                }}
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--mc-warning-icon)" }}
                >
                  <AlertCircle className="w-3 h-3 text-white" />
                </div>
                <span
                  className="text-sm font-medium"
                  style={{ color: "var(--mc-warning-icon)" }}
                >
                  {metrics.overdueInvoices} Overdue Invoice
                  {metrics.overdueInvoices !== 1 ? "s" : ""}
                </span>
                <span
                  className="text-sm"
                  style={{ color: "var(--mc-warning-icon)" }}
                >
                  · {formatCurrency(metrics.overdueAmount, "full")} requiring
                  attention
                </span>
              </div>
            </>
          )}

          {/* Overdue + Upcoming cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-neutral-200 px-3 py-2 text-center">
              <p className="text-base font-semibold text-neutral-900">
                {metrics.overdueInvoices}
              </p>
              <p className="text-xs text-neutral-900 mt-0.5">
                Overdue Invoices
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {formatCurrency(metrics.overdueAmount, "full")}
              </p>
            </div>
            <div className="rounded-lg border border-neutral-200 px-3 py-2 text-center">
              <p className="text-base font-semibold text-neutral-900">
                {metrics.upcomingPayments}
              </p>
              <p className="text-xs text-neutral-900 mt-0.5">
                Upcoming Payments
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {formatCurrency(metrics.upcomingAmount, "full")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <CashBreakdownModal
        open={showCashModal}
        onClose={() => setShowCashModal(false)}
        accounts={DEMO_BANK_ACCOUNTS}
        totalCash={metrics.currentCash}
      />
    </>
  );
}
